/**
 * Template Cache Service
 *
 * Handles local caching of templates from remote sources (Google Drive).
 * Implements local-first architecture where all editing happens on local copies.
 */

import { Template, TemplateSourceType } from '../types'

// Strip legacy gdrive_ prefix from stored remoteId values
function normalizeRemoteId(remoteId: string): string {
  if (remoteId.startsWith('gdrive_')) return remoteId.slice(7)
  return remoteId
}

export interface CacheCheckResult {
  hasLocalCopy: boolean
  localTemplate?: Template
  localId?: string
  needsUpdate: boolean // Remote is newer than local
  remoteModified?: string // Remote file's last modified timestamp
}

export interface RemoteTemplateInfo {
  id: string
  fileName: string
  fileSize: number
  modifiedTime: string
  createdTime?: string
  path?: string
  folderPath?: string
}

/**
 * Check if a local copy exists for a remote template and if it needs updating
 */
export async function checkCacheStatus(
  remoteId: string,
  sourceType: TemplateSourceType,
): Promise<CacheCheckResult> {
  try {
    // Get local templates only (source=local bypasses remote listing)
    const response = await fetch('/api/templates?source=local')
    if (!response.ok) {
      return { hasLocalCopy: false, needsUpdate: true }
    }

    const localTemplates: Template[] = await response.json()

    // Find local template with matching remote ID (normalize to handle legacy prefixed IDs)
    const localTemplate = localTemplates.find((t) => {
      const storedId = t.source?.remoteId ? normalizeRemoteId(t.source.remoteId) : null
      if (storedId === remoteId) return true
      // Check legacy fields for backward compatibility
      if (sourceType === 'gdrive' && t.googleDriveId === remoteId) return true
      return false
    })

    if (!localTemplate) {
      return { hasLocalCopy: false, needsUpdate: true }
    }

    // Get remote file info to compare timestamps
    const remoteInfo = await getRemoteFileInfo(remoteId, sourceType)

    if (!remoteInfo) {
      // Remote file no longer exists, but we have local copy
      return {
        hasLocalCopy: true,
        localTemplate,
        localId: localTemplate.id,
        needsUpdate: false,
      }
    }

    // Compare timestamps
    const localModified =
      localTemplate.source?.lastSyncedAt ||
      localTemplate.source?.importedAt ||
      localTemplate.lastModified?.toString()
    const remoteModified = remoteInfo.modifiedTime

    const needsUpdate =
      localModified && remoteModified ? new Date(remoteModified) > new Date(localModified) : false

    return {
      hasLocalCopy: true,
      localTemplate,
      localId: localTemplate.id,
      needsUpdate,
      remoteModified,
    }
  }
  catch (error) {
    console.error('Error checking cache status:', error)
    return { hasLocalCopy: false, needsUpdate: true }
  }
}

/**
 * Get info about a remote file without downloading it
 */
async function getRemoteFileInfo(
  remoteId: string,
  _sourceType: TemplateSourceType,
): Promise<RemoteTemplateInfo | null> {
  try {
    // Route directly by remote ID — the server uses isUUID to distinguish local vs remote
    console.log(`[templateCache] Getting remote file info: /api/templates/${remoteId}`)
    const response = await fetch(`/api/templates/${remoteId}`)
    if (!response.ok) {
      console.warn(
        `[templateCache] Remote file info failed: ${response.status} ${response.statusText}`,
      )
      return null
    }
    return response.json()
  }
  catch (error) {
    console.warn('[templateCache] Error fetching remote file info:', error)
    return null
  }
}

/**
 * Ensure a local copy exists for a remote template.
 * Downloads and caches if needed, returns the local template.
 */
export async function ensureLocalCopy(
  remoteId: string,
  sourceType: TemplateSourceType,
  onProgress?: (message: string) => void,
  templateMeta?: { folderPath?: string },
): Promise<Template> {
  onProgress?.('Checking local cache...')

  const cacheStatus = await checkCacheStatus(remoteId, sourceType)

  if (cacheStatus.hasLocalCopy && !cacheStatus.needsUpdate && cacheStatus.localTemplate) {
    onProgress?.('Using cached copy')
    return cacheStatus.localTemplate
  }

  // Need to download from remote
  onProgress?.('Downloading from Google Drive...')

  const remoteTemplate = await downloadRemoteTemplate(remoteId, sourceType)

  // Merge in additional metadata from the caller (e.g., folderPath from list)
  if (templateMeta?.folderPath) {
    remoteTemplate.metadata.folderPath = templateMeta.folderPath
  }

  if (cacheStatus.hasLocalCopy && cacheStatus.localId) {
    // Update existing local copy
    onProgress?.('Updating local copy...')
    return await updateLocalTemplate(cacheStatus.localId, remoteTemplate, sourceType, remoteId)
  }
  else {
    // Create new local copy
    onProgress?.('Saving to local storage...')
    return await createLocalCopy(remoteTemplate, sourceType, remoteId)
  }
}

/**
 * Download a template from a remote source
 */
async function downloadRemoteTemplate(
  remoteId: string,
  _sourceType: TemplateSourceType,
): Promise<{ pdfData: ArrayBuffer; metadata: any }> {
  // Route directly by remote ID — the server uses isUUID to distinguish local vs remote
  // Get metadata
  console.log(`[templateCache] Fetching metadata: /api/templates/${remoteId}`)
  const metaResponse = await fetch(`/api/templates/${remoteId}`)
  if (!metaResponse.ok) {
    const errorBody = await metaResponse.text().catch(() => '')
    console.error(
      `[templateCache] Metadata fetch failed: ${metaResponse.status} ${metaResponse.statusText}`,
      errorBody,
    )
    throw new Error(
      `Failed to fetch template metadata (${metaResponse.status}): ${errorBody || metaResponse.statusText}`,
    )
  }
  const metadata = await metaResponse.json()

  // Get PDF data
  console.log(`[templateCache] Downloading PDF: /api/templates/${remoteId}/pdf`)
  const pdfResponse = await fetch(`/api/templates/${remoteId}/pdf`)
  if (!pdfResponse.ok) {
    const errorBody = await pdfResponse.text().catch(() => '')
    console.error(
      `[templateCache] PDF download failed: ${pdfResponse.status} ${pdfResponse.statusText}`,
      errorBody,
    )
    throw new Error(
      `Failed to download PDF (${pdfResponse.status}): ${errorBody || pdfResponse.statusText}`,
    )
  }
  const pdfData = await pdfResponse.arrayBuffer()
  console.log(`[templateCache] Downloaded ${pdfData.byteLength} bytes`)

  return { pdfData, metadata }
}

/**
 * Create a new local copy of a remote template
 */
async function createLocalCopy(
  remoteData: { pdfData: ArrayBuffer; metadata: any },
  sourceType: TemplateSourceType,
  remoteId: string,
): Promise<Template> {
  const { pdfData, metadata } = remoteData

  // Create a File object from the PDF data
  const blob = new Blob([pdfData], { type: 'application/pdf' })
  const file = new File([blob], metadata.fileName || 'template.pdf', { type: 'application/pdf' })

  // Upload to local storage
  const formData = new FormData()
  formData.append('file', file)
  formData.append('clientName', metadata.clientName || '')
  formData.append('programName', metadata.programName || '')

  // Include source info for the server to embed in metadata
  formData.append('sourceType', sourceType)
  formData.append('sourceRemoteId', remoteId)
  formData.append('sourceRemotePath', metadata.folderPath || metadata.path || '')
  formData.append('sourceRemoteModified', metadata.modifiedTime || metadata.lastModified || '')

  const response = await fetch('/api/templates/upload', {
    method: 'POST',
    body: formData,
  })

  if (!response.ok) {
    throw new Error(`Failed to create local copy: ${response.statusText}`)
  }

  const result = await response.json()

  // Fetch the full template to return
  const templateResponse = await fetch(`/api/templates/${result.id}`)
  if (!templateResponse.ok) {
    throw new Error('Failed to fetch created template')
  }

  return templateResponse.json()
}

/**
 * Update an existing local template with fresh data from remote
 */
async function updateLocalTemplate(
  localId: string,
  remoteData: { pdfData: ArrayBuffer; metadata: any },
  sourceType: TemplateSourceType,
  remoteId: string,
): Promise<Template> {
  const { pdfData, metadata } = remoteData

  // Create a File object from the PDF data
  const blob = new Blob([pdfData], { type: 'application/pdf' })
  const file = new File([blob], metadata.fileName || 'template.pdf', { type: 'application/pdf' })

  // Use the replace PDF endpoint
  const formData = new FormData()
  formData.append('file', file)

  const response = await fetch(`/api/templates/${localId}/pdf`, {
    method: 'PUT',
    body: formData,
  })

  if (!response.ok) {
    throw new Error(`Failed to update local copy: ${response.statusText}`)
  }

  // Update source metadata
  await fetch(`/api/templates/${localId}/source`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      type: sourceType,
      remoteId,
      remotePath: metadata.folderPath || metadata.path || '',
      remoteModified: metadata.modifiedTime || metadata.lastModified || '',
      lastSyncedAt: new Date().toISOString(),
    }),
  })

  // Fetch the updated template to return
  const templateResponse = await fetch(`/api/templates/${localId}`)
  if (!templateResponse.ok) {
    throw new Error('Failed to fetch updated template')
  }

  return templateResponse.json()
}

/**
 * Find a local template by its remote ID
 */
export async function getLocalByRemoteId(
  remoteId: string,
  sourceType: TemplateSourceType,
): Promise<Template | null> {
  const cacheStatus = await checkCacheStatus(remoteId, sourceType)
  return cacheStatus.localTemplate || null
}

/**
 * Get sync status for display in UI
 */
export function getSyncStatus(template: Template): 'local' | 'synced' | 'modified' | 'remote' {
  if (!template.source) {
    // Check legacy fields
    if (template.isGoogleDrive) {
      return 'synced' // Assume synced if using legacy fields
    }
    return 'local'
  }

  if (template.source.type === 'local') {
    return 'local'
  }

  if (!template.source.lastSyncedAt) {
    return 'remote' // Not yet cached locally
  }

  // TODO: Compare with actual remote timestamp to detect local modifications
  return 'synced'
}

/**
 * True for local templates (UUID or 20-char base62 push ID format) — the
 * same check the server uses (isLocalId() in server/routes/storage.js) to
 * route a template ID to the local storage provider.
 */
export function isLocalTemplateId(templateId: string): boolean {
  return (
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(templateId) ||
    /^[0-9A-Za-z]{20}$/.test(templateId)
  )
}

/**
 * Parse a template ID to determine if it's a remote template.
 * Local templates use UUID format or 20-char base62 push IDs;
 * anything else is a remote ID routed to the configured storage backend.
 */
export function parseTemplateId(
  templateId: string,
  storageMode?: string,
): {
  sourceType: TemplateSourceType
  remoteId: string
} | null {
  if (isLocalTemplateId(templateId)) return null
  // Non-local = remote template, route based on configured storage mode
  const sourceType = (storageMode || 'gdrive') as TemplateSourceType
  return { sourceType, remoteId: templateId }
}
